export interface RateLimitResult {
  allowed: boolean;
  delay: number;
}

export class RateLimitService {
  private attemptsMap: Map<string, { attempts: number; lastAttempt: number }>;
  private delays: number[];
  private resetAfter: number;

  /**
   * @param delays Array of progressive delays in ms (e.g. [1000, 2000, 5000])
   * @param resetAfter Time in ms after which attempts are reset (e.g. 3600000 for 1 hour)
   */
  constructor(delays: number[], resetAfter: number = 3600000) {
    this.attemptsMap = new Map();
    this.delays = delays;
    this.resetAfter = resetAfter;
  }

  /**
   * Check if an action is allowed for a given key (e.g. username)
   */
  check(key: string): RateLimitResult {
    const now = Date.now();
    const entry = this.attemptsMap.get(key);

    if (!entry) {
      return { allowed: true, delay: 0 };
    }

    if (now - entry.lastAttempt > this.resetAfter) {
      this.attemptsMap.delete(key);
      return { allowed: true, delay: 0 };
    }

    const delay = this.delays[Math.min(entry.attempts, this.delays.length - 1)] ?? 1000;
    if (now - entry.lastAttempt < delay) {
      return { allowed: false, delay: delay - (now - entry.lastAttempt) };
    }

    return { allowed: true, delay: 0 };
  }

  /**
   * Record a failed attempt for a given key
   */
  record(key: string): void {
    const now = Date.now();
    const entry = this.attemptsMap.get(key);
    if (entry) {
      entry.attempts += 1;
      entry.lastAttempt = now;
    } else {
      this.attemptsMap.set(key, { attempts: 1, lastAttempt: now });
    }
  }

  /**
   * Clear rate limiting for a given key (e.g. on successful login)
   */
  clear(key: string): void {
    this.attemptsMap.delete(key);
  }
}
